import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Country } from 'country-state-city';
import * as dashboardApi from '../../api/dashboardApi';
import { useTheme } from '../../context/ThemeContext';
import { Eyebrow, PageHeading } from '../../design-system';
import styles from './RegulationsBreakdownPage.module.css';

type SortKey = 'name' | 'count';
type SortDir = 'asc' | 'desc';

const EXTRA_NAMES: Record<string, string> = {
  EU: 'European Union',
  UN: 'United Nations',
  OECD: 'OECD',
};

const SUPRANATIONALS_FULL: Record<string, string> = {
  EU: 'European Union',
  UN: 'United Nations',
  OECD: 'OECD',
  GCC: 'Gulf Cooperation Council',
  ASEAN: 'ASEAN',
  APEC: 'APEC',
  AU: 'African Union',
};

function getCountryName(iso2: string): string {
  return EXTRA_NAMES[iso2] || Country.getCountryByCode(iso2)?.name || iso2;
}

const Arrow: React.FC<{ dir: SortDir | null }> = ({ dir }) => (
  <span style={{ marginLeft: 3, opacity: dir ? 1 : 0.3, fontSize: '0.65rem' }}>
    {dir === 'asc' ? '▲' : '▼'}
  </span>
);

const RegulationsBreakdownPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  const backRoute = location.pathname.startsWith('/admin/dashboard')
    ? '/admin/dashboard/charts'
    : location.pathname.startsWith('/enterprise/dashboard')
      ? '/enterprise/dashboard/charts'
      : location.pathname.startsWith('/startup/dashboard')
        ? '/startup/dashboard/charts'
        : location.pathname.startsWith('/government/dashboard')
          ? '/government/dashboard/charts'
          : '/dashboard';

  const [allJurisdictions, setAllJurisdictions] = useState<dashboardApi.Jurisdiction[]>([]);
  const [stats, setStats] = useState<dashboardApi.JurisdictionStats[]>([]);
  const [byCountry, setByCountry] = useState<Record<string, number>>({});
  const [totalRegulations, setTotalRegulations] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const [countrySortKey, setCountrySortKey] = useState<SortKey>('count');
  const [countrySortDir, setCountrySortDir] = useState<SortDir>('desc');
  const [jurisSortKey, setJurisSortKey] = useState<SortKey>('count');
  const [jurisSortDir, setJurisSortDir] = useState<SortDir>('desc');
  const [countrySearch, setCountrySearch] = useState('');
  const [jurisSearch, setJurisSearch] = useState('');

  useEffect(() => {
    Promise.all([
      dashboardApi.getJurisdictionStats({}),
      dashboardApi.getJurisdictions({ limit: 500 }),
      dashboardApi.getTotalRegulations(),
    ]).then(([statsRes, jurisdictions, totalRes]) => {
      setStats(statsRes?.stats ?? []);
      setByCountry(statsRes?.by_country ?? {});
      setAllJurisdictions(jurisdictions ?? []);
      setTotalRegulations((totalRes as any)?.total ?? null);
    }).finally(() => setLoading(false));
  }, []);

  // Build a map: jurisdiction_code → regulation_count from stats
  const statsByCode = useMemo(() => {
    const m: Record<string, number> = {};
    stats.forEach(s => { m[s.jurisdiction_code] = s.regulation_count; });
    return m;
  }, [stats]);

  // All jurisdictions merged with counts (0 if no stats)
  const jurisRows = useMemo(() => {
    const rows = allJurisdictions
      .map(j => ({
        id: j.id,
        name: j.name,
        code: j.code,
        country_name: j.country_name || '',
        continent_name: j.continent_name || '',
        count: statsByCode[j.code] ?? 0,
      }))
      .filter(r =>
        r.name.toLowerCase().includes(jurisSearch.toLowerCase()) ||
        r.code.toLowerCase().includes(jurisSearch.toLowerCase()) ||
        r.country_name.toLowerCase().includes(jurisSearch.toLowerCase())
      );
    rows.sort((a, b) => {
      const val = jurisSortKey === 'name' ? a.name.localeCompare(b.name) : a.count - b.count;
      return jurisSortDir === 'asc' ? val : -val;
    });
    return rows;
  }, [allJurisdictions, statsByCode, jurisSortKey, jurisSortDir, jurisSearch]);

  // Only countries that actually have regulations (from byCountry).
  // Exclude codes that are neither a valid ISO-2 country nor a known supranational
  // (e.g. internal codes like "XE" that may leak from the backend).
  const countryRows = useMemo(() => {
    const rows = Object.entries(byCountry)
      .map(([iso2, count]) => ({
        iso2: iso2.toUpperCase(),
        name: getCountryName(iso2.toUpperCase()),
        count,
      }))
      .filter(r => EXTRA_NAMES[r.iso2] || !!Country.getCountryByCode(r.iso2))
      .filter(r =>
        r.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
        r.iso2.toLowerCase().includes(countrySearch.toLowerCase())
      );
    rows.sort((a, b) => {
      const val = countrySortKey === 'name' ? a.name.localeCompare(b.name) : a.count - b.count;
      return countrySortDir === 'asc' ? val : -val;
    });
    return rows;
  }, [byCountry, countrySortKey, countrySortDir, countrySearch]);

  // Supranational bodies derived from per-jurisdiction stats (works without backend restart)
  const supraRows = useMemo(() => {
    const counts: Record<string, number> = {};
    stats.forEach(s => {
      const prefix = (s.jurisdiction_code || '').split('-')[0].toUpperCase();
      if (SUPRANATIONALS_FULL[prefix]) {
        counts[prefix] = (counts[prefix] || 0) + s.regulation_count;
      }
    });
    return Object.entries(SUPRANATIONALS_FULL)
      .filter(([code]) => (counts[code] ?? 0) > 0)
      .map(([code, name]) => ({ code, name, count: counts[code] }))
      .sort((a, b) => b.count - a.count);
  }, [stats]);

  const grandTotal = totalRegulations ?? Object.values(byCountry).reduce((s, v) => s + v, 0);
  const maxCount = countryRows.length > 0 ? Math.max(...countryRows.map(r => r.count), 1) : 1;

  const toggleCountry = (key: SortKey) => {
    if (countrySortKey === key) setCountrySortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCountrySortKey(key); setCountrySortDir('desc'); }
  };
  const toggleJuris = (key: SortKey) => {
    if (jurisSortKey === key) setJurisSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setJurisSortKey(key); setJurisSortDir('desc'); }
  };

  return (
    <div className={styles.page} data-theme={theme}>
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(backRoute)}>
          ← Back
        </button>
        <div className={styles.titleBlock}>
          <Eyebrow tone="default" dot>REGULATIONS</Eyebrow>
          <PageHeading
            accentWord="Breakdown"
            accentTone="orange"
            size="h2"
            align="left"
            level="h1"
            className={styles.title}
          >
            Regulations Breakdown
          </PageHeading>
          <p className={styles.subtitle}>
            {loading ? '…' : `${grandTotal.toLocaleString()} regulations across ${countryRows.length} countries and ${allJurisdictions.length} jurisdictions`}
          </p>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingWrap}>
          <div className={styles.spinner} />
          <span>Loading breakdown…</span>
        </div>
      ) : (
        <div className={styles.stack}>

          {/* ── By Country ── */}
          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>By Country</h2>
              <span className={styles.badge}>{countryRows.length}</span>
              <input
                className={styles.search}
                placeholder="Search country…"
                value={countrySearch}
                onChange={e => setCountrySearch(e.target.value)}
              />
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <colgroup>
                  <col className={styles.colRank} />
                  <col />
                  <col className={styles.colCode} />
                  <col className={styles.colCount} />
                  <col className={styles.colShare} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={styles.th}>#</th>
                    <th className={`${styles.th} ${styles.sortable}`} onClick={() => toggleCountry('name')}>
                      Country <Arrow dir={countrySortKey === 'name' ? countrySortDir : null} />
                    </th>
                    <th className={styles.th}>Code</th>
                    <th className={`${styles.th} ${styles.sortable} ${styles.right}`} onClick={() => toggleCountry('count')}>
                      Regs <Arrow dir={countrySortKey === 'count' ? countrySortDir : null} />
                    </th>
                    <th className={`${styles.th} ${styles.right}`}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {countryRows.map((row, i) => {
                    const pct = grandTotal > 0 ? (row.count / grandTotal) * 100 : 0;
                    const barPct = maxCount > 0 ? (row.count / maxCount) * 100 : 0;
                    return (
                      <tr key={row.iso2} className={styles.tr}>
                        <td className={styles.rankCell}>{i + 1}</td>
                        <td className={styles.td} title={row.name}>{row.name}</td>
                        <td className={styles.td}><span className={styles.code}>{row.iso2}</span></td>
                        <td className={`${styles.td} ${styles.right}`}>
                          <strong>{row.count.toLocaleString()}</strong>
                        </td>
                        <td className={styles.td}>
                          <div className={styles.barCell}>
                            <div className={styles.barTrack}>
                              <div className={styles.barFill} style={{ width: `${barPct}%` }} />
                            </div>
                            <span className={styles.pct}>{pct.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {countryRows.length === 0 && (
                    <tr><td colSpan={5} className={styles.empty}>No results</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── By Supranationals ── */}
          {supraRows.length > 0 && (
            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>By Supranational Body</h2>
                <span className={styles.badge}>{supraRows.length}</span>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <colgroup>
                    <col className={styles.colRank} />
                    <col />
                    <col className={styles.colCode} />
                    <col className={styles.colCount} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className={styles.th}>#</th>
                      <th className={styles.th}>Body</th>
                      <th className={styles.th}>Code</th>
                      <th className={`${styles.th} ${styles.right}`}>Regs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supraRows.map((row, i) => (
                      <tr key={row.code} className={styles.tr}>
                        <td className={styles.rankCell}>{i + 1}</td>
                        <td className={styles.td}>{row.name}</td>
                        <td className={styles.td}><span className={styles.code}>{row.code}</span></td>
                        <td className={`${styles.td} ${styles.right}`}>
                          <strong>{row.count.toLocaleString()}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── By Jurisdiction ── */}
          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>By Jurisdiction</h2>
              <span className={styles.badge}>{jurisRows.length}</span>
              <input
                className={styles.search}
                placeholder="Search jurisdiction…"
                value={jurisSearch}
                onChange={e => setJurisSearch(e.target.value)}
              />
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <colgroup>
                  <col className={styles.colRank} />
                  <col />
                  <col className={styles.colCountry} />
                  <col className={styles.colCount} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={styles.th}>#</th>
                    <th className={`${styles.th} ${styles.sortable}`} onClick={() => toggleJuris('name')}>
                      Jurisdiction <Arrow dir={jurisSortKey === 'name' ? jurisSortDir : null} />
                    </th>
                    <th className={styles.th}>Country</th>
                    <th className={`${styles.th} ${styles.sortable} ${styles.right}`} onClick={() => toggleJuris('count')}>
                      Regs <Arrow dir={jurisSortKey === 'count' ? jurisSortDir : null} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {jurisRows.map((row, i) => (
                    <tr key={row.id} className={styles.tr}>
                      <td className={styles.rankCell}>{i + 1}</td>
                      <td className={styles.td}>
                        <div className={styles.nameCell}>
                          <span title={row.name}>{row.name}</span>
                          <span className={styles.code}>{row.code}</span>
                        </div>
                      </td>
                      <td className={styles.td}>
                        <div className={styles.nameCell}>
                          <span>{row.country_name || '—'}</span>
                        </div>
                      </td>
                      <td className={`${styles.td} ${styles.right}`}>
                        <strong>{row.count.toLocaleString()}</strong>
                      </td>
                    </tr>
                  ))}
                  {jurisRows.length === 0 && (
                    <tr><td colSpan={4} className={styles.empty}>No results</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

        </div>
      )}
    </div>
  );
};

export default RegulationsBreakdownPage;
